import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, CheckCircle2, XCircle, ExternalLink, MessageSquare, History, AlertTriangle } from "lucide-react";

export default function WhatsAppBroadcastPage() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    campaign_name: "",
    numbers_raw: "",
    template_name: "",
    template_params: "",
  });

  const loadAll = () => Promise.all([
    api.get("/admin/whatsapp/api-status").then((r) => setStatus(r.data)),
    api.get("/admin/whatsapp/broadcasts?limit=20").then((r) => setHistory(r.data)).catch(() => setHistory([])),
  ]);
  useEffect(() => { loadAll(); }, []);

  const send = async () => {
    const numbers = form.numbers_raw
      .split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean)
      .map((n) => n.replace(/[^0-9]/g, ""))
      .filter((n) => n.length >= 10);
    if (numbers.length === 0) return toast.error("कम से कम एक valid mobile number दें (10 digits या country code के साथ)");
    const params = form.template_params
      ? form.template_params.split(/\n/).map((s) => s.trim()).filter(Boolean)
      : [];
    setBusy(true);
    try {
      const { data } = await api.post("/admin/whatsapp/api-broadcast", {
        campaign_name: form.campaign_name || null,
        template_name: form.template_name || null,
        numbers, template_params: params,
      });
      if (data.configured === false) {
        toast.error("AiSensy keys backend .env में सेट करें");
      } else if (data.ok) {
        toast.success(`✅ Broadcast भेजा — ${data.sent}/${data.total} · Failed: ${data.failed_count}`);
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="wa-broadcast-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">WhatsApp Business API</div>
        <h1 className="font-devnagri-heading text-2xl md:text-3xl font-black text-[#0F172A]">Broadcast Campaigns</h1>
        <p className="text-sm text-slate-500 mt-1">एक साथ 500+ subscribers को WhatsApp पर template message भेजें · AiSensy powered</p>
      </div>

      {/* Config status */}
      {status && (
        <div className={`rounded-xl border-2 p-4 flex items-start gap-3 ${status.configured ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          {status.configured ? <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0"/> : <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0"/>}
          <div className="min-w-0 flex-1">
            {status.configured ? (
              <>
                <div className="font-black text-emerald-900">✅ AiSensy API configured</div>
                <div className="text-xs text-emerald-800 mt-1">Default campaign: <b>{status.default_campaign || "—"}</b> · Sender: <b>{status.user_name}</b></div>
              </>
            ) : (
              <>
                <div className="font-black text-amber-900">⚠️ Setup pending — API keys ज़रूरी हैं</div>
                <div className="text-xs text-amber-800 mt-1">Backend <code className="bg-white px-1 py-0.5 rounded">.env</code> में <code>AISENSY_API_KEY</code> और <code>AISENSY_CAMPAIGN_NAME</code> add करें, फिर backend restart करें।</div>
                <div className="flex gap-3 mt-2 text-xs">
                  <a href={status.setup_url} target="_blank" rel="noopener noreferrer" className="font-black text-amber-900 underline flex items-center gap-1" data-testid="wa-signup">
                    <ExternalLink className="w-3 h-3"/> AiSensy पर signup करें
                  </a>
                  <a href={status.docs} target="_blank" rel="noopener noreferrer" className="font-black text-amber-900 underline flex items-center gap-1" data-testid="wa-docs">
                    <ExternalLink className="w-3 h-3"/> API Docs
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Broadcast form */}
      <div className="rounded-xl bg-white border-2 border-slate-100 overflow-hidden">
        <div className="px-4 py-3 border-b-2 border-slate-100 bg-slate-50 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-emerald-600"/>
          <div className="font-devnagri-heading text-lg font-black text-[#0F172A]">नया Broadcast</div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-slate-500">Campaign / Template Name (optional)</label>
            <input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
              placeholder={status?.default_campaign || "news_breaking_v1"}
              className="mt-1 w-full text-sm border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-500 focus:outline-none"
              data-testid="wa-campaign"/>
            <div className="text-[10px] text-slate-500 mt-1">खाली छोड़ें तो default campaign use होगा ({status?.default_campaign || "—"})</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-slate-500">Numbers (comma / newline separated · 91 prefix के साथ)</label>
            <textarea value={form.numbers_raw} onChange={(e) => setForm({ ...form, numbers_raw: e.target.value })}
              rows={4}
              placeholder="919876543210, 918888888888&#10;917777777777"
              className="mt-1 w-full text-sm border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-500 focus:outline-none font-mono"
              data-testid="wa-numbers"/>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-black text-slate-500">Template Params (एक params per line · ordered)</label>
            <textarea value={form.template_params} onChange={(e) => setForm({ ...form, template_params: e.target.value })}
              rows={3}
              placeholder="Jakir Momin&#10;सूरजपुर में बड़ी दुर्घटना&#10;https://indiannews21.com/article/xyz"
              className="mt-1 w-full text-sm border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-500 focus:outline-none"
              data-testid="wa-params"/>
            <div className="text-[10px] text-slate-500 mt-1">आपके AiSensy template में जितने {"{{1}}, {{2}}"} placeholders हैं, उतने lines add करें</div>
          </div>
          <button onClick={send} disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black py-3 rounded-lg disabled:opacity-50"
            data-testid="wa-send">
            <Send className="w-4 h-4"/> {busy ? "भेज रहे हैं…" : "Broadcast भेजें"}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="rounded-xl bg-white border-2 border-slate-100 overflow-hidden">
        <div className="px-4 py-3 border-b-2 border-slate-100 bg-slate-50 flex items-center gap-2">
          <History className="w-5 h-5 text-slate-500"/>
          <div className="font-devnagri-heading text-lg font-black text-[#0F172A]">Broadcast History ({history.length})</div>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">कोई broadcast history नहीं। पहला भेजने पर यहाँ दिखेगा।</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map((h) => (
              <li key={h.id || h.audit_id} className="px-4 py-3 flex items-center gap-3">
                {h.failed_count === 0 ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0"/> : <XCircle className="w-5 h-5 text-red-500 shrink-0"/>}
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[#0F172A] truncate">{h.campaign} · <span className="text-slate-500 text-xs">{h.template_name || "—"}</span></div>
                  <div className="text-[11px] text-slate-500">Sent: <b>{h.sent}/{h.total}</b> · Failed: <b>{h.failed_count}</b> · {h.created_at ? new Date(h.created_at).toLocaleString("hi-IN") : "—"}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
